/**
 * Kanban Board
 *
 * Task-specific wrapper around the generic <Board> primitive. Wires up
 * status-transition validation, mutation dispatch, and the completed-column
 * timeframe filter.
 *
 * @module features/tasks/components/KanbanBoard
 */

import React, { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Board } from '../../../ui/components/board';
import type {
  BoardColumnConfig,
  BoardDropEvent,
  BoardDropResult,
} from '../../../ui/components/board';
import { boardSurfaceClassName } from '../../boards/BoardToolbar';
import {
  moveAcrossColumnLists,
  rollbackMove,
  type MoveSnapshot,
  type MoveTarget,
} from '../../boards/optimisticMove';
import { kanbanColumnOfKey, TASKS_LIST_PREFIX, useKanbanTasks } from '../hooks/useKanbanTasks';
import {
  useTriggerTask,
  useClaimTask,
  useCompleteTask,
  useCancelTask,
  useMoveTask,
  useReopenTask,
  useReleaseTask,
} from '../hooks/useTasks';
import type { AgentTaskListResponse } from '../types';
import {
  KANBAN_COLUMNS,
  STATUS_LABELS,
  COLUMN_COLORS,
  COLUMN_DROP_COLORS,
  VALID_TRANSITIONS,
  TRANSITION_ACTIONS,
} from '../constants';
import type { AgentTask, AgentTaskStatus } from '../types';
import { KanbanCard } from './KanbanCard';
import { FilterableColumnHeader } from './FilterableColumnHeader';

const NON_DRAGGABLE_STATUSES = new Set<AgentTaskStatus>(['waiting', 'expired']);

const TIMEFRAME_OPTIONS = [
  { id: '24h', label: 'Last 24 hours' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: 'Last week' },
  { id: '30d', label: 'Last month' },
  { id: 'all', label: 'All time' },
];

interface KanbanBoardProps {
  onCardClick: (taskId: string) => void;
  completedUpdatedAfter?: string;
  search?: string;
  agentSubmitted?: 'true' | 'false';
  tagIds?: string[];
  excludeTagIds?: string[];
  priorities?: number[];
  accountCardId?: string;
  createdBy?: string;
  completedTimeframe: string;
  onCompletedTimeframeChange: (value: string) => void;
}

export function KanbanBoard({
  onCardClick,
  completedUpdatedAfter,
  search,
  agentSubmitted,
  tagIds,
  excludeTagIds,
  priorities,
  accountCardId,
  createdBy,
  completedTimeframe,
  onCompletedTimeframeChange,
}: KanbanBoardProps) {
  const columnData = useKanbanTasks({
    completedUpdatedAfter,
    search,
    agentSubmitted,
    tagIds,
    excludeTagIds,
    priorities,
    accountCardId,
    createdBy,
  });
  const queryClient = useQueryClient();
  const triggerTask = useTriggerTask();
  const claimTask = useClaimTask();
  const completeTask = useCompleteTask();
  const cancelTask = useCancelTask();
  const reopenTask = useReopenTask();
  const releaseTask = useReleaseTask();
  const moveTask = useMoveTask();
  const pending = useRef(new Set<string>());

  /**
   * Mirrors the drop into every cached per-column query (all filter
   * variants): the source column drops the task, the target column splices it
   * at the dropped slot, and header counts stay consistent.
   */
  const applyOptimisticTaskMove = useCallback(
    (
      item: AgentTask,
      fromColumnId: AgentTaskStatus,
      toColumnId: AgentTaskStatus,
      target: MoveTarget
    ): MoveSnapshot =>
      moveAcrossColumnLists<AgentTaskListResponse, AgentTask>(queryClient, {
        prefixKey: TASKS_LIST_PREFIX,
        matchColumn: kanbanColumnOfKey,
        fromColumnId,
        toColumnId,
        item,
        updateItem: (t) => ({ ...t, status: toColumnId }),
        target,
        getId: (t) => t.id,
        readList: (r) => (Array.isArray(r?.data) ? r.data : null),
        writeList: (r, list, delta) => ({
          ...r,
          data: list,
          pagination: {
            ...r.pagination,
            totalItems: Math.max(0, (r.pagination?.totalItems ?? list.length) + delta),
          },
        }),
      }),
    [queryClient]
  );

  const columns: BoardColumnConfig<AgentTaskStatus>[] = KANBAN_COLUMNS.map((col) => ({
    id: col.id,
    label: STATUS_LABELS[col.id],
    accentClass: COLUMN_COLORS[col.id],
    dropClass: COLUMN_DROP_COLORS[col.id],
    isLoading: columnData[col.id]?.isLoading ?? true,
    totalItems: columnData[col.id]?.totalItems ?? 0,
    emptyLabel: 'No tasks',
    headerExtra:
      col.id === 'completed' ? (
        <FilterableColumnHeader
          label=""
          options={TIMEFRAME_OPTIONS}
          value={completedTimeframe}
          onChange={onCompletedTimeframeChange}
        />
      ) : undefined,
  }));

  const itemsByColumn = KANBAN_COLUMNS.reduce(
    (acc, col) => {
      acc[col.id] = columnData[col.id]?.tasks ?? [];
      return acc;
    },
    {} as Record<AgentTaskStatus, AgentTask[]>
  );

  const validateDrop = useCallback(
    (from: AgentTaskStatus, to: AgentTaskStatus) =>
      from === to ? true : (VALID_TRANSITIONS[from] ?? []).includes(to),
    []
  );

  const handleDrop = useCallback(
    ({
      item,
      fromColumnId,
      toColumnId,
      beforeItemId,
      afterItemId,
    }: BoardDropEvent<AgentTaskStatus, AgentTask>): BoardDropResult => {
      if (pending.current.has(item.id)) return false;
      const target: MoveTarget = { beforeItemId, afterItemId };

      const finish = () => {
        pending.current.delete(item.id);
        void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      };

      // Same-column: persist the manual order via /tasks/:id/move.
      if (fromColumnId === toColumnId) {
        if (!beforeItemId && !afterItemId) return false; // no neighbor to anchor to
        pending.current.add(item.id);
        void queryClient.cancelQueries({ queryKey: TASKS_LIST_PREFIX });
        const snapshot = applyOptimisticTaskMove(item, fromColumnId, toColumnId, target);
        moveTask.mutate(
          { id: item.id, beforeItemId, afterItemId },
          {
            onError: () => rollbackMove(queryClient, snapshot),
            onSettled: finish,
          }
        );
        return;
      }

      const action = TRANSITION_ACTIONS[`${fromColumnId}->${toColumnId}`];
      if (!action) return false;

      pending.current.add(item.id);
      void queryClient.cancelQueries({ queryKey: TASKS_LIST_PREFIX });
      const snapshot = applyOptimisticTaskMove(item, fromColumnId, toColumnId, target);

      const onError = () => {
        rollbackMove(queryClient, snapshot);
        finish();
      };
      // The transition lands the task at the BOTTOM of the target column
      // server-side; refine to the dropped slot when there's a neighbor to
      // anchor to. A failed refine is benign (right column, bottom slot).
      const onSuccess = () => {
        if (!beforeItemId && !afterItemId) return finish();
        void queryClient.cancelQueries({ queryKey: TASKS_LIST_PREFIX });
        moveTask.mutate({ id: item.id, beforeItemId, afterItemId }, { onSettled: finish });
      };
      const callbacks = { onSuccess, onError };

      switch (action) {
        case 'trigger':
          triggerTask.mutate(item.id, callbacks);
          break;
        case 'claim':
          claimTask.mutate(item.id, callbacks);
          break;
        case 'complete':
          completeTask.mutate({ id: item.id }, callbacks);
          break;
        case 'cancel':
          cancelTask.mutate(item.id, callbacks);
          break;
        case 'reopen':
          reopenTask.mutate(
            { id: item.id, status: toColumnId as 'actionable' | 'in_progress' },
            callbacks
          );
          break;
        case 'release':
          releaseTask.mutate(
            { id: item.id, status: toColumnId as 'actionable' | 'waiting' },
            callbacks
          );
          break;
      }
    },
    [
      queryClient,
      applyOptimisticTaskMove,
      moveTask,
      triggerTask,
      claimTask,
      completeTask,
      cancelTask,
      reopenTask,
      releaseTask,
    ]
  );

  return (
    <Board<AgentTaskStatus, AgentTask>
      className={boardSurfaceClassName}
      columns={columns}
      itemsByColumn={itemsByColumn}
      getItemId={(t) => t.id}
      getItemColumnId={(t) => t.status}
      isItemDraggable={(t) => !NON_DRAGGABLE_STATUSES.has(t.status)}
      validateDrop={validateDrop}
      onDrop={handleDrop}
      onCardClick={(t) => onCardClick(t.id)}
      renderCard={(task, { isOverlay }) => (
        <KanbanCard
          task={task}
          isOverlay={isOverlay}
          nonDraggable={NON_DRAGGABLE_STATUSES.has(task.status)}
        />
      )}
    />
  );
}
